import { moment } from '@seiue/moment'
import {
  Badge,
  Button,
  ButtonIcon,
  Ellipsis,
  Popover,
  State,
  Toast,
} from '@seiue/ui'
import { compact } from '@seiue/util'
import { Empty } from 'packages/components/Empty'
import { GrowpIcon } from 'packages/components/GrowpIcon'
import { $t } from 'packages/locale'
import {
  NoticeDataTypeEnum,
  NoticeDetail,
  notificationApi,
  pluginApi,
  PluginNameEnum,
} from 'packages/sdks-next/growp'
import { AnyType } from 'packages/types'
import { FC, useState } from 'react'
import styled from 'styled-components'

import { Flex, FlexVertical, H6 } from './components/Styled'

/**
 * 消息提醒
 *
 * @returns - 组件
 */
export const LayoutHeaderRight: FC = () => {
  const {
    data: notices,
    reload,
    loading,
  } = notificationApi.useQueryFrontendNotices()
  const [visible, setVisible] = useState(false)

  return (
    <Popover
      destroyTooltipOnHide={true}
      visible={visible}
      onVisibleChange={setVisible}
      placement="bottomRight"
      trigger="click"
      align={{ offset: [0, 24] }}
      content={
        <NoticeDetailsModalContent
          noticesDetails={notices?.details || []}
          reload={reload}
          onClose={() => {
            setVisible(false)
          }}
          loading={loading}
        />
      }
    >
      <Badge
        count={notices?.unreadedCount}
        size="small"
      >
        <MessageIcon icon="Remind" />
      </Badge>
    </Popover>
  )
}

const NoticeDetailsModalContent: FC<{
  noticesDetails: NoticeDetail[]
  reload: () => void
  onClose: () => void
  loading?: boolean
}> = ({ noticesDetails, reload, onClose, loading }) => {
  const disabledReadAll = !noticesDetails.some((item) => !item.isRead)

  const sortedNoticesDetails = noticesDetails.sort((a, b) => {
    // 先按已读/未读排序：未读排前面
    if (a.isRead !== b.isRead) {
      return a.isRead ? 1 : -1
    }

    // 再按时间排序：startTime 降序
    return moment(b.startTime).unix() - moment(a.startTime).unix()
  })

  return (
    <Wrapper>
      <Header
        $justify="space-between"
        style={{ padding: '12px 20px' }}
      >
        <H6>{$t('消息提醒')}</H6>
        <Flex>
          <ReadAllButton
            size="small"
            type="link"
            disabled={disabledReadAll}
            tooltip={disabledReadAll ? $t('没有未读消息') : undefined}
            onClick={async () => {
              const req = compact(
                sortedNoticesDetails.map((item) =>
                  item.uid && item.dataType
                    ? {
                        uid: item.uid,
                        dataType: item.dataType,
                      }
                    : undefined
                )
              )

              await notificationApi.readNotice(req)
              reload()
              Toast.success($t('全部忽略成功'))
            }}
          >
            {$t('全部忽略')}
          </ReadAllButton>
          <IconWrapper>
            <GrowpIcon
              onClick={onClose}
              name="Close"
              size={16}
            />
          </IconWrapper>
        </Flex>
      </Header>
      <Content>
        {loading ? (
          <State.Loading />
        ) : sortedNoticesDetails.length ? (
          sortedNoticesDetails.map((item) => {
            // 获取到的真实发件人字符串格式："\xxx\" <example@xx.coom>
            const fromMatch = item.from?.match(/"?(.*?)"?\s*<(.+?)>/)

            return (
              <Badge
                key={item.uid}
                size="small"
                dot={!item.isRead}
                offset={[-20, 24]}
              >
                <Item
                  onClick={async () => {
                    if (!item.isRead && item.uid && item.dataType) {
                      notificationApi
                        .readNotice([
                          {
                            uid: item.uid,
                            dataType: item.dataType,
                          },
                        ])
                        .then(() => {
                          reload()
                        })
                    }
                    const url = await getLinkByNoticeType({
                      noticeType: item.noticeType,
                      dataType: item.dataType,
                      uid: item.uid,
                    })

                    if (url) {
                      window.open(url, '_blank')
                    } else {
                      Toast.info(`打开${getTypeLabel(item.noticeType)}查看详情`)
                    }
                  }}
                >
                  <Flex>
                    <MainLabel $isRead={!!item.isRead}>
                      {getTypeLabel(item.noticeType)}
                    </MainLabel>
                    <SubLabel $isRead={!!item.isRead}>{`${$t(
                      '有一条新的'
                    )}${getLabelByNoticeDataType(item.dataType)}`}</SubLabel>
                    <Label
                      text={
                        item.startTime && item.endTime
                          ? `${getTimeLabel({
                              startAt: item.startTime,
                              endAt: item.endTime,
                              dataType: item.dataType,
                            })} ${item.title}`
                          : item.title
                      }
                      $isRead={!!item.isRead}
                    />
                  </Flex>
                  {item.from || item.sentDate || item.address ? (
                    <Flex style={{ flexWrap: 'wrap' }}>
                      {item.from ? (
                        <SubLabel $isRead={!!item.isRead}>{`发件人：${
                          fromMatch?.[1] || item.from || ''
                        } ${fromMatch?.[2] || ''}`}</SubLabel>
                      ) : null}
                      {item.sentDate ? (
                        <SubLabel $isRead={!!item.isRead}>
                          {moment(item.sentDate).format('YYYY-MM-DD HH:mm:ss')}
                        </SubLabel>
                      ) : null}
                      {item.address ? (
                        <SubLabel $isRead={!!item.isRead}>{`${$t('地点：')}${
                          item.address
                        }`}</SubLabel>
                      ) : null}
                    </Flex>
                  ) : null}
                </Item>
              </Badge>
            )
          })
        ) : (
          <Empty />
        )}
      </Content>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: 480px;
  height: 340px;
  overflow: hidden;
`

const Header = styled(Flex)`
  // 添加下阴影
  box-shadow: 0px 0px 3px 0px rgba(21, 29, 41, 0.2),
    0px 3px 8px -1px rgba(21, 29, 41, 0.04);
`

const MessageIcon = styled(ButtonIcon)`
  &&& {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: ${(props) => props.theme.background._3};
  }
`

const IconWrapper = styled.div`
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  &:hover {
    background-color: ${(props) => props.theme.background._3};
  }
`

const Item = styled(FlexVertical)`
  padding: 12px;
  gap: 12px;
  border-bottom: 1px solid ${(props) => props.theme.border.greyLight};
  cursor: pointer;
  &:hover {
    background-color: ${(props) => props.theme.background._3};
    border-radius: 8px;
  }
`

const MainLabel = styled.div<{ $isRead?: boolean }>`
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  color: ${(props) => props.theme.text._1};
  flex-shrink: 0;
  text-decoration: ${(props) => (props.$isRead ? 'line-through' : 'none')};
`

const Label = styled(Ellipsis)<{ $isRead?: boolean }>`
  font-size: 14px;
  line-height: 22px;
  color: ${(props) => props.theme.text._1};
  max-width: 254px;
  text-decoration: ${(props) => (props.$isRead ? 'line-through' : 'none')};
`
const SubLabel = styled.div<{ $isRead?: boolean }>`
  font-size: 14px;
  line-height: 22px;
  color: ${(props) => props.theme.text._2};
  flex-shrink: 0;
  text-decoration: ${(props) => (props.$isRead ? 'line-through' : 'none')};
`

const ReadAllButton = styled(Button)`
  margin-right: 0;
`

const Content = styled.div`
  height: calc(100% - 52px);
  overflow: auto;
  padding: 8px;
  .ant-badge {
    width: 100%;
  }
`

const getTypeLabel = (noticeType?: PluginNameEnum | null) => {
  switch (noticeType) {
    case PluginNameEnum.Nocobase:
      return 'Nocobase'
    case PluginNameEnum.Oa:
      return 'OA'
    case PluginNameEnum.Qymail163:
      return $t('企业邮箱')
    case PluginNameEnum.Qywx:
      return $t('企业微信')
    case PluginNameEnum.Seiue:
      return $t('希悦')
    default:
      return '平台'
  }
}

const getLinkByNoticeType = async ({
  noticeType,
  dataType,
  uid,
}: {
  noticeType?: PluginNameEnum | null
  dataType?: NoticeDataTypeEnum | null
  uid?: string | null
}) => {
  switch (noticeType) {
    case PluginNameEnum.Oa:
      return 'http://oa.pkuschool.edu.cn/general/index.php'
    case PluginNameEnum.Qymail163:
      // 如果是邮件则跳转到邮件，否则跳转到首页
      const mid = dataType === NoticeDataTypeEnum.Event ? '' : uid || ''
      try {
        const { data: url } = await pluginApi.mailLoginUrlForNotice({
          mid,
        })
        return url
      } catch (error: AnyType) {
        const { data } = error.response
        Toast.error(data?.message)
        return dataType === NoticeDataTypeEnum.Event
          ? 'https://mail.pkuschool.edu.cn/static/sirius-web/#schedule'
          : 'https://mail.pkuschool.edu.cn/static/sirius-web/#mailbox'
      }
    case PluginNameEnum.Seiue:
      return getSeiueLinkByDataType(dataType)
    default:
      return ''
  }
}

const getSeiueLinkByDataType = (dataType?: NoticeDataTypeEnum | null) => {
  switch (dataType) {
    case NoticeDataTypeEnum.Todo:
      return 'https://chalk-c3.seiue.com/todos'
    case NoticeDataTypeEnum.Message:
      return 'https://chalk-c3.seiue.com/notices/inbox'
    default:
      return 'https://chalk-c3.seiue.com'
  }
}

const getLabelByNoticeDataType = (dataType?: NoticeDataTypeEnum | null) => {
  switch (dataType) {
    case NoticeDataTypeEnum.Email:
      return $t('邮件')
    case NoticeDataTypeEnum.Event:
      return $t('日程')
    case NoticeDataTypeEnum.Message:
      return $t('通知')
    case NoticeDataTypeEnum.Todo:
      return $t('待办')
    default:
      return ''
  }
}

const getTimeLabel = ({
  startAt,
  endAt,
  dataType,
}: {
  startAt: string
  endAt: string
  dataType?: NoticeDataTypeEnum | null
}) => {
  if (dataType === NoticeDataTypeEnum.Message) return ''
  const isSameDate = moment(startAt).isSame(endAt, 'day')
  if (!isSameDate)
    return `${moment(startAt).format('YYYY-MM-DD HH:mm')}~${moment(
      endAt
    ).format('YYYY-MM-DD HH:mm')}`

  const start = moment(startAt).format('HH:mm')
  const end = moment(endAt).format('HH:mm')

  if (start === end) return `${moment(startAt).format('YYYY-MM-DD')} ${start}`

  return `${moment(startAt).format('YYYY-MM-DD')} ${start}-${end}`
}
